import { useState, useEffect, useMemo } from "react";
import { useBranches } from "@/hooks/useBranches";
import { useUsersApi } from "@/hooks/useUsersApi";
import { useAuth } from "@/hooks/useAuth";

export function useGetAvailableBranches() {
  const { branches } = useBranches();
  const { getUserByEmail } = useUsersApi();
  const { user } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [userBrands, setUserBrands] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  useEffect(() => {
    const checkUserType = async () => {
      if (user?.email) {
        try {
          const userData = await getUserByEmail(user.email);

          const isUserAdmin = Boolean(userData?.userType === "admin");
          setIsAdmin(isUserAdmin);

          // Si el usuario no es admin, obtener sus marcas asignadas
          if (!isUserAdmin && userData?.roleSpecificData?.brands) {
            setUserBrands(userData.roleSpecificData.brands);
          } else if (isUserAdmin) {
            setUserBrands([]);
          }
        } catch (error) {
          setIsAdmin(false);
          setUserBrands([]);
        } finally {
          setIsLoadingUser(false);
        }
      } else {
        setIsLoadingUser(false);
      }
    };

    checkUserType();
  }, [user?.email, getUserByEmail]);

  // Calcular las sucursales disponibles según las marcas accesibles
  const availableBranches = useMemo(() => {
    if (isAdmin) {
      return branches;
    } else {
      const accessibleBrandIds = userBrands.map((brand) => brand.id);
      const filteredBranches = branches.filter((branch) =>
        accessibleBrandIds.includes(branch.brand_id),
      );
      return filteredBranches;
    }
  }, [isAdmin, branches, userBrands]);

  return {
    availableBranches,
    isAdmin,
    userBrands,
    isLoadingUser,
  };
}
